import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Card, 
    TableContainer, 
    Table, 
    TableBody, 
    TablePagination, 
    Pagination, 
    TextField, 
    Switch, 
    Typography, 
    InputAdornment, 
    IconButton, 
    Chip 
} from '@mui/material';
import { 
    Search as SearchIcon, 
    Clear as ClearIcon, 
    VisibilityOutlined as VisibilityOutlinedIcon, 
    EditOutlined as EditOutlinedIcon, 
    DeleteOutlineOutlined as DeleteOutlineOutlinedIcon, 
    RestoreOutlined as RestoreOutlinedIcon 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTableState } from 'hooks/useTableState';
import { deleteServicesIaCustomerSuscriptionStore, getAllServicesIaCustomerSuscriptionsStore, restoreServicesIaCustomerSuscriptionStore } from 'store/services-ia-customer-suscriptions/thunks';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { corregirTexto, firstLetterUpperCase, hasPemissions, usarFuncionesComposicion } from 'commons/utils';
import { paddingCardsTables } from 'commons/values';

import { GenericTableHead, HeadCell } from 'commons/generics/GenericTableHead';
import { StyledTableRow, StyledTableCell } from 'commons/generics/StyledTableComponents';
import { SinDatos } from 'commons/generics/SinDatos';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';

const headCells: HeadCell[] = [
    { id: 'plan', label: 'Plan' },
    { id: 'cliente', label: 'Cliente' },
    { id: 'monthlyCredits', label: 'Créditos Mensuales' },
    { id: 'remainingCredits', label: 'Créditos Restantes' },
    { id: 'startsAt', label: 'Fecha Inicio' },
    { id: 'expiresAt', label: 'Fecha Expiración' },
    { id: 'status', label: 'Estado' },
    { id: 'actions', label: 'Acciones', align: 'center' }
];

export const TableSuscripciones = () => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { suscriptions_list } = useSelector((state: any) => state.servicesIaSuscriptions);
    const { handleResponse, ResponseComponents } = useResponseHandler();

    const {
        dataTableList,
        canView, setCanView,
        canEdit, setCanEdit,
        canDelete, setCanDelete,
        canRestore, setCanRestore,
        openElem, setOpenElem,
        checked,
        searchTerm, setSearchTerm,
        order, orderBy,
        handleSwitchChange,
        handleSearch: handleSearchHook,
        handleResetSearch: handleResetSearchHook,
        page, rowsPerPage,
        handleChangePage, handleChangeRowsPerPage,
        pagePagination, itemCountPagination,
        changePaginationPage: changePaginationPageHook,
        handleRequestSort,
        updatePaginationFromList,
        openModalDeactivation, setOpenModalDeactivation,
        openModalRestore, setOpenModalRestore,
        getSortedAndFilteredList,
        isServerSide
    } = useTableState('suscriptions_list');

    const [hasPermission, setHasPermission] = useState(true);
    const [inputValue, setInputValue] = useState('');

    const checkPermissions = async () => {
        const view = await hasPemissions('modulo.manager', 'modulo.manager.plataforma.ia', 'view', 'modulo.manager.plataforma.ia.comercial', 'modulo.manager.plataforma.ia.comercial.suscripciones');
        if (!view) setHasPermission(false);
        else setCanView(true);

        const edit = await hasPemissions('modulo.manager', 'modulo.manager.plataforma.ia', 'edit', 'modulo.manager.plataforma.ia.comercial', 'modulo.manager.plataforma.ia.comercial.suscripciones');
        if (edit) setCanEdit(true);

        const del = await hasPemissions('modulo.manager', 'modulo.manager.plataforma.ia', 'delete', 'modulo.manager.plataforma.ia.comercial', 'modulo.manager.plataforma.ia.comercial.suscripciones');
        if (del) {
            setCanDelete(true);
            setCanRestore(true);
        }
    };

    useEffect(() => {
        checkPermissions();
    }, []);

    const getInitialData = async (statusOverride?: number) => {
        const currentStatus = statusOverride !== undefined ? statusOverride : (checked ? 0 : 1);
        const response = await dispatch(getAllServicesIaCustomerSuscriptionsStore({ page: 1, take: rowsPerPage, status: currentStatus, param: inputValue }));
        await handleResponse(response, 'obtener suscripciones', false);
    };

    useEffect(() => {
        if (canView) {
            getInitialData();
        }
    }, [canView]);

    useEffect(() => {
        updatePaginationFromList(suscriptions_list);
    }, [suscriptions_list, updatePaginationFromList]);

    const handleSearch = async (param: string) => {
        setSearchTerm(param);
        setInputValue(param);
        handleSearchHook(param, dispatch, (p: string) => getAllServicesIaCustomerSuscriptionsStore({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }), getInitialData, null, 'buscar suscripciones');
    };

    const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSwitchChange(e);
        const newChecked = e.target.checked;
        const status = newChecked ? 0 : 1;
        
        if (searchTerm) {
            handleSearchHook(searchTerm, dispatch, (p: string) => getAllServicesIaCustomerSuscriptionsStore({ page: 1, take: rowsPerPage, status, param: p }), () => getInitialData(status), null, 'buscar suscripciones');
        } else {
            getInitialData(status);
        }
    };

    const onResetSearch = () => {
        setInputValue('');
        handleResetSearchHook(() => getInitialData());
    };

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        changePaginationPageHook(event, value);
        const response = await dispatch(getAllServicesIaCustomerSuscriptionsStore({ page: value, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'listar suscripciones', false);
    };

    const confirmDeactivate = async () => {
        if (!openElem) return;
        const response = await dispatch(deleteServicesIaCustomerSuscriptionStore(openElem.id, { page, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'desactivar suscripción', true);
        setOpenModalDeactivation(false);
        setOpenElem(null);
    };

    const confirmRestore = async () => {
        if (!openElem) return;
        const response = await dispatch(restoreServicesIaCustomerSuscriptionStore(openElem.id, { page, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'restaurar suscripción', true);
        setOpenModalRestore(false);
        setOpenElem(null);
    };

    const handleView = (row: any) => {
        navigate(`/manager/plataforma-ia/comercial/suscripciones/${row.id}/detalle`);
    };

    const handleEdit = (row: any) => {
        navigate(`/manager/plataforma-ia/comercial/suscripciones/${row.id}/editar`);
    };

    const sortedList = getSortedAndFilteredList(
        dataTableList || [], 
        ['planName'],
        'status', 
        checked ? 0 : 1
    );

    const formatDate = (date: any) => {
        if (!date) return 'No definido';
        return new Date(date).toLocaleDateString('es-ES');
    };

    return (
        <Box sx={{ width: '100%', mt: paddingCardsTables }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <TextField
                    placeholder="Buscar..."
                    variant="outlined"
                    size="small"
                    autoComplete='off'
                    value={inputValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ width: 400 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: inputValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch checked={checked} onChange={onSwitchChange} color="primary" />
                    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                        Mostrar solo inactivas
                    </Typography>
                </Box>  
            </Box>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <GenericTableHead 
                            headCells={headCells} 
                            order={order} 
                            orderBy={orderBy} 
                            onRequestSort={handleRequestSort} 
                        />
                        <TableBody>
                            {sortedList.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={headCells.length} align="center">
                                        <SinDatos />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                sortedList
                                    .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                                    .map((row: any) => (
                                        <StyledTableRow key={row.id}>
                                            <StyledTableCell>
                                                <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.plan?.name)}
                                                </Box>
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.customer?.label || row.customerId || 'No definido')}
                                            </StyledTableCell>
                                            
                                            <StyledTableCell>
                                                {row.monthlyCredits}
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                {row.remainingCredits}
                                            </StyledTableCell>
                                            
                                            <StyledTableCell>
                                                {formatDate(row.startsAt)}
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                {formatDate(row.expiresAt)}
                                            </StyledTableCell>
                                            
                                            <StyledTableCell>
                                                <Chip
                                                    label={row.status === 1 ? 'Activa' : 'Inactiva'}
                                                    color={row.status === 1 ? 'success' : 'error'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </StyledTableCell>
                                            
                                            <StyledTableCell align="center">
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                    <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => canView && handleView(row)} 
                                                            disabled={!canView} 
                                                            color="info"
                                                        >
                                                            <VisibilityOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                    
                                                    <span style={{ cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer' }}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => canEdit && row.status === 1 && handleEdit(row)} 
                                                            disabled={!canEdit || row.status !== 1} 
                                                            color="primary"
                                                        >
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                    
                                                    {row.status === 1 ? (
                                                        <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => {
                                                                    if (canDelete) {
                                                                        setOpenElem(row);
                                                                        setOpenModalDeactivation(true);
                                                                    }
                                                                }} 
                                                                disabled={!canDelete} 
                                                                color="error"
                                                            >
                                                                <DeleteOutlineOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    ) : (
                                                        <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => {
                                                                    if (canRestore) {
                                                                        setOpenElem(row);
                                                                        setOpenModalRestore(true);
                                                                    }
                                                                }} 
                                                                disabled={!canRestore} 
                                                                color="success"
                                                            >
                                                                <RestoreOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    )}
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {dataTableList.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={dataTableList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Filas por página"
                    />
                )}

                {dataTableList.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
                        <Pagination
                            sx={{ justifyContent: 'center' }}
                            color="primary"
                            count={itemCountPagination}
                            page={pagePagination}
                            onChange={changePaginationPage} 
                            showFirstButton 
                            showLastButton
                            shape="circular"
                        />
                    </Box>
                )}
            </Card>

            <ModalConfirmDeactivation
                open={openModalDeactivation} 
                setOpen={setOpenModalDeactivation} 
                title='¿Estás seguro que deseas desactivar esta suscripción?'
                description='Esta suscripción pasará a estado inactivo y no podrá ser utilizada.'
                onConfirm={confirmDeactivate}
            />
            
            <ModalConfirmRestore
                open={openModalRestore}
                setOpen={setOpenModalRestore}
                title='¿Estás seguro que deseas restaurar esta suscripción?'
                description='Esta suscripción pasará a estado activo.'
                onConfirm={confirmRestore}
            />

            {ResponseComponents}
        </Box>
    );
};
